import { cn, createIconElement } from '@/lib';
import IcClose from '@/assets/icons/ic-x-close.svg';

interface SuggestedFunnelPageNavRowProps {
    stepNumber: number;
    title: string;
    active: boolean;
    onSelect: () => void;
    /** Optional dismiss handler — renders an X button that removes the row from the nav. */
    onDismiss?: () => void;
}

export const SuggestedFunnelPageNavRow = ({
    stepNumber,
    title,
    active,
    onSelect,
    onDismiss,
}: SuggestedFunnelPageNavRowProps) => {
    const handleDismiss = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onDismiss?.();
    };

    return (
        <div
            role='button'
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect();
                }
            }}
            aria-current={active ? 'step' : undefined}
            className={cn(
                'group flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors',
                active ? 'bg-brand-subtle' : 'hover:bg-subtle'
            )}
        >
            <span className='text-caption-2-bold text-gray w-4 shrink-0 text-center tabular-nums'>{stepNumber}</span>
            <div
                className={cn(
                    'bg-canvas text-brand-main text-caption-1-bold flex size-10 shrink-0 items-center justify-center rounded-md border border-dashed transition-all',
                    active ? 'border-brand-main bg-brand-subtle ring-brand-selected ring-2' : 'border-brand-selected'
                )}
            >
                +
            </div>
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <span className='text-caption-2-bold text-gray uppercase'>Page {stepNumber}</span>
                <span className='text-heading-h5 text-dark truncate'>{title}</span>
            </div>
            {onDismiss && (
                <button
                    type='button'
                    onClick={handleDismiss}
                    aria-label={`Remove suggested page ${stepNumber}`}
                    className='text-gray hover:bg-neutral-subtle hover:text-dark flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100'
                >
                    {createIconElement(IcClose, { className: 'size-4' })}
                </button>
            )}
        </div>
    );
};
